import { RequirementObservations } from '../../../components/v2/teacher/RequirementObservations';
import { useTeacherWorkspace } from '../../../components/layout/TeacherWorkspace';
import React, { useEffect, useState } from 'react';
import { artifactReviewApi, evidenceApi } from '../../../services/apiV2';
import {
  Evidence,
  OfficialArtifactReviewGroup,
  OfficialArtifactReviewItem,
  OfficialArtifactReviewQueue,
} from '../../../types/v2';
import TeacherNav from '../../../components/TeacherNav';
import { AppShell } from '../../../components/layout/AppShell';
import { teacherV2NavItems } from '../../../components/layout/v2NavItems';
import { isUiRedesignPageEnabled } from '../../../config/uiRedesign';

const EMPTY_ARTIFACT_QUEUE: OfficialArtifactReviewQueue = {
  total: 0,
  summary: {},
  groups: [],
  items: [],
};

const ARTIFACT_STATUS_ORDER = [
  'submitted',
  'under_review',
  'needs_revision',
  'ready_to_submit',
  'coaching_flagged',
  'drafting',
  'approved',
];

const formatLabel = (value?: string | null) => {
  const rawValue = String(value || '').trim();
  if (!rawValue) {
    return '';
  }
  return rawValue
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return '';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString();
};

const formatLegacyContent = (item: Evidence) => {
  const textValue = typeof item.content?.text === 'string' ? item.content.text.trim() : '';
  const urlValue = typeof item.content?.url === 'string' ? item.content.url.trim() : '';
  if (textValue) {
    return textValue;
  }
  if (urlValue) {
    return urlValue;
  }
  const serialized = JSON.stringify(item.content || {}, null, 2);
  return serialized === '{}' ? 'No content provided.' : serialized;
};

const OfficialArtifactCard: React.FC<{
  item: OfficialArtifactReviewItem;
  noteValue: string;
  isSaving: boolean;
  onNoteChange: (value: string) => void;
  onAction: (action: 'reviewing' | 'approve' | 'request_revision') => void;
}> = ({ item, noteValue, isSaving, onNoteChange, onAction }) => {
  const coachSession = item.latest_coach_session;
  const showReviewingAction = item.status === 'submitted';
  const showDecisionActions = item.status === 'submitted' || item.status === 'under_review';

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="text-base font-semibold text-slate-900">{item.student_name}</h4>
            <span className="rounded-full bg-slate-200 px-2.5 py-1 text-xs font-medium text-slate-700">
              {formatLabel(item.status)}
            </span>
          </div>
          <p className="text-sm text-slate-600">{item.student_email}</p>
          {item.artifact_prompt ? (
            <p className="text-sm text-slate-700">
              <span className="font-medium text-slate-900">Prompt:</span> {item.artifact_prompt}
            </p>
          ) : null}
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600">
            {item.source_ref ? <span>Assignment: {item.source_ref}</span> : null}
            {item.submitted_at ? <span>Submitted: {formatDateTime(item.submitted_at)}</span> : null}
            {item.reviewed_at ? <span>Reviewed: {formatDateTime(item.reviewed_at)}</span> : null}
            {item.returned_at ? <span>Returned: {formatDateTime(item.returned_at)}</span> : null}
          </div>
          {item.source_url ? (
            <a
              className="inline-flex text-sm font-medium text-sky-700 underline"
              href={item.source_url}
              target="_blank"
              rel="noreferrer"
            >
              Open submission source
            </a>
          ) : null}
        </div>
        <div className="w-full max-w-xl space-y-3">
          {coachSession ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950">
              <p className="font-medium">Latest draft coach: {formatLabel(coachSession.status)}</p>
              {coachSession.student_feedback ? (
                <p className="mt-1">{coachSession.student_feedback}</p>
              ) : null}
              {coachSession.suggestions && coachSession.suggestions.length > 0 ? (
                <p className="mt-1 text-amber-900">
                  Suggestions: {coachSession.suggestions.join(' | ')}
                </p>
              ) : null}
            </div>
          ) : null}
          <div>
            <label
              className="mb-1 block text-sm font-medium text-slate-700"
              htmlFor={`artifact-note-${item.id}`}
            >
              Teacher note for {item.student_name}
            </label>
            <textarea
              id={`artifact-note-${item.id}`}
              className="min-h-[96px] w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200"
              value={noteValue}
              onChange={(event) => onNoteChange(event.target.value)}
              placeholder="Add context before marking this artifact under review, approved, or returned."
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {showReviewingAction ? (
              <button
                type="button"
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-800 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
                onClick={() => onAction('reviewing')}
                disabled={isSaving}
              >
                Mark under review
              </button>
            ) : null}
            {showDecisionActions ? (
              <button
                type="button"
                className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                onClick={() => onAction('approve')}
                disabled={isSaving}
              >
                Approve after reviewing in Headrush
              </button>
            ) : null}
            {showDecisionActions ? (
              <button
                type="button"
                className="rounded-lg border border-amber-300 bg-white px-3 py-2 text-sm font-medium text-amber-800 transition hover:bg-amber-50 disabled:cursor-not-allowed disabled:opacity-60"
                onClick={() => onAction('request_revision')}
                disabled={isSaving}
              >
                Request revision
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

const OfficialArtifactGroupCard: React.FC<{
  group: OfficialArtifactReviewGroup;
  actionId: number | null;
  noteValues: Record<number, string>;
  onNoteChange: (itemId: number, value: string) => void;
  onAction: (item: OfficialArtifactReviewItem, action: 'reviewing' | 'approve' | 'request_revision') => void;
}> = ({ group, actionId, noteValues, onNoteChange, onAction }) => (
  <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
    <div className="flex flex-col gap-3 border-b border-slate-200 pb-4 md:flex-row md:items-start md:justify-between">
      <div>
        <h3 className="text-lg font-semibold text-slate-900">{group.lesson_step_title}</h3>
        <p className="mt-1 text-sm text-slate-600">
          {group.section_name} | {group.hook_title}
          {group.stage_tag ? ` | ${formatLabel(group.stage_tag)}` : ''}
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        {ARTIFACT_STATUS_ORDER.filter((statusKey) => group.status_counts[statusKey]).map((statusKey) => (
          <span
            key={`${group.lesson_step_id}-${statusKey}`}
            className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700"
          >
            {formatLabel(statusKey)} {group.status_counts[statusKey]}
          </span>
        ))}
      </div>
    </div>
    <div className="mt-4 space-y-4">
      {group.items.map((item) => (
        <OfficialArtifactCard
          key={item.id}
          item={item}
          noteValue={noteValues[item.id] ?? item.teacher_note ?? ''}
          isSaving={actionId === item.id}
          onNoteChange={(value) => onNoteChange(item.id, value)}
          onAction={(action) => onAction(item, action)}
        />
      ))}
    </div>
  </section>
);

export const V2EvidenceReview: React.FC = () => {
  const workspace = useTeacherWorkspace();
  const selectedClass = Number(workspace?.selected) || undefined;
  const redesignEnabled = isUiRedesignPageEnabled('v2_teacher_review');
  const [artifactQueue, setArtifactQueue] = useState<OfficialArtifactReviewQueue>(EMPTY_ARTIFACT_QUEUE);
  const [legacyEvidence, setLegacyEvidence] = useState<Evidence[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [artifactActionId, setArtifactActionId] = useState<number | null>(null);
  const [legacyActionId, setLegacyActionId] = useState<number | null>(null);
  const [notesByItemId, setNotesByItemId] = useState<Record<number, string>>({});

  const loadQueues = async () => {
    setLoading(true);
    setErrorMessage('');
    try {
      const [artifactPayload, legacyResponse] = await Promise.all([
        artifactReviewApi.reviewQueue({ section_id: selectedClass }),
        evidenceApi.reviewQueue(),
      ]);
      setArtifactQueue(artifactPayload || EMPTY_ARTIFACT_QUEUE);
      setLegacyEvidence(Array.isArray(legacyResponse?.data?.evidence) ? legacyResponse.data.evidence.filter((item: any) => !selectedClass || item.section_id === selectedClass || item.current_section_ids?.includes(selectedClass)) : []);
    } catch {
      setErrorMessage('Could not load review queues.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadQueues();
  }, []);

  const updateNote = (itemId: number, value: string) => {
    setNotesByItemId((current) => ({
      ...current,
      [itemId]: value,
    }));
  };

  const handleArtifactAction = async (
    item: OfficialArtifactReviewItem,
    action: 'reviewing' | 'approve' | 'request_revision',
  ) => {
    setArtifactActionId(item.id);
    setErrorMessage('');
    const payload = { teacher_note: notesByItemId[item.id] ?? item.teacher_note ?? '' };
    try {
      if (action === 'reviewing') {
        await artifactReviewApi.markReviewing(item.id, payload);
      } else if (action === 'approve') {
        await artifactReviewApi.approve(item.id, payload);
      } else {
        await artifactReviewApi.requestRevision(item.id, payload);
      }
      await loadQueues();
    } catch {
      setErrorMessage('Could not update official artifact status.');
    } finally {
      setArtifactActionId(null);
    }
  };

  const handleLegacyDecision = async (evidenceId: number, decision: 'approved' | 'needs_revision') => {
    setLegacyActionId(evidenceId);
    setErrorMessage('');
    try {
      await evidenceApi.submitFeedback(evidenceId, { decision });
      await loadQueues();
    } catch {
      setErrorMessage('Could not update legacy evidence review.');
    } finally {
      setLegacyActionId(null);
    }
  };

  const pageContent = (
    <div className={redesignEnabled ? 'mx-auto max-w-6xl' : 'mx-auto max-w-6xl p-6'}>
      <div className="space-y-2">
        <h1 className="text-2xl font-semibold text-slate-900">Official Artifact Review</h1>
        <p className="text-sm text-slate-600">
          Review Headrush-backed Apply artifacts here. Legacy internal evidence remains available below during migration.
        </p>
      </div>

      <RequirementObservations key={selectedClass || 'all'} sectionId={selectedClass} />

      <div className="mt-4 flex flex-wrap gap-2">
        {artifactQueue.total > 0 ? (
          ARTIFACT_STATUS_ORDER.filter((statusKey) => artifactQueue.summary[statusKey]).map((statusKey) => (
            <span
              key={`summary-${statusKey}`}
              className="rounded-full bg-slate-200 px-3 py-1 text-sm font-medium text-slate-800"
            >
              {formatLabel(statusKey)} {artifactQueue.summary[statusKey]}
            </span>
          ))
        ) : (
          <span className="rounded-full bg-slate-200 px-3 py-1 text-sm font-medium text-slate-800">
            No official artifacts awaiting review
          </span>
        )}
      </div>

      {errorMessage ? (
        <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          {errorMessage}
        </div>
      ) : null}

      <section className="mt-6 space-y-4">
        <div className="flex items-end justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Official artifacts</h2>
            <p className="text-sm text-slate-600">Submitted, in review, and returned Apply artifacts grouped by lesson step.</p>
          </div>
        </div>
        {loading ? (
          <div className="rounded-2xl border border-slate-200 bg-white px-4 py-6 text-sm text-slate-600 shadow-sm">
            Loading review queue...
          </div>
        ) : artifactQueue.groups.length > 0 ? (
          artifactQueue.groups.map((group) => (
            <OfficialArtifactGroupCard
              key={`${group.section_id}-${group.lesson_step_id}`}
              group={group}
              actionId={artifactActionId}
              noteValues={notesByItemId}
              onNoteChange={updateNote}
              onAction={handleArtifactAction}
            />
          ))
        ) : (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-8 text-sm text-slate-600 shadow-sm">
            No official artifacts need review right now.
          </div>
        )}
      </section>

      <section className="mt-8 space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Legacy internal evidence</h2>
          <p className="text-sm text-slate-600">These items still use the older Elevate-only evidence review flow.</p>
        </div>
        {loading ? null : legacyEvidence.length > 0 ? (
          <div className="space-y-4">
            {legacyEvidence.map((item) => (
              <div key={item.id} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-base font-semibold text-slate-900">{item.student_name || (item.student ? `Student #${item.student}` : 'Student not recorded')}</h3>
                      <span className="rounded-full bg-slate-200 px-2.5 py-1 text-xs font-medium text-slate-700">
                        {formatLabel(item.status)}
                      </span>
                    </div>
                    <p className="text-sm text-slate-700">
                      {item.hook_title || 'Hook not available'} · {item.stage ? formatLabel(item.stage) : 'Stage not recorded'}
                    </p>
                    <p className="text-sm text-slate-700">
                      Lesson: {item.lesson_plan_title || 'Not recorded'} · Step: {item.lesson_step_title || 'Not recorded'}
                    </p>
                    <p className="text-sm text-slate-600">
                      Current class context: {(item.current_section_names || (item.section_name ? [item.section_name] : [])).join(', ') || 'Not available'}.
                      {' '}Submission class was not recorded.
                    </p>
                    <p className="text-sm text-slate-600">Submitted: {formatDateTime(item.submitted_at) || 'Date not recorded'}</p>
                    <p className="whitespace-pre-wrap text-sm text-slate-700">
                      <span className="font-medium">Current step prompt:</span> {item.current_prompt || 'Not available'}
                    </p>
                    <p className="text-xs text-slate-500">The original prompt was not saved with this legacy submission.</p>
                    <p className="text-sm font-medium text-slate-900">Submitted response</p>
                    <pre className="whitespace-pre-wrap rounded-lg bg-slate-50 p-3 text-sm text-slate-800">
                      {formatLegacyContent(item)}
                    </pre>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                      onClick={() => handleLegacyDecision(item.id, 'approved')}
                      disabled={legacyActionId === item.id}
                    >
                      Approve
                    </button>
                    <button
                      type="button"
                      className="rounded-lg border border-amber-300 bg-white px-3 py-2 text-sm font-medium text-amber-800 transition hover:bg-amber-50 disabled:cursor-not-allowed disabled:opacity-60"
                      onClick={() => handleLegacyDecision(item.id, 'needs_revision')}
                      disabled={legacyActionId === item.id}
                    >
                      Needs revision
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-8 text-sm text-slate-600 shadow-sm">
            No legacy internal evidence is waiting for review.
          </div>
        )}
      </section>
    </div>
  );

  return redesignEnabled ? (
    <AppShell role="teacher" navItems={teacherV2NavItems('review')} title="Review">
      {pageContent}
    </AppShell>
  ) : (
    <>
      <TeacherNav activeTab="review" />
      {pageContent}
    </>
  );
};
